import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getSplitSupplyPlanAvailability } from '@ebalance/proto/bffuser/v1/split_supply_plan_service-SplitSupplyPlanService_connectquery'
import { GetSplitSupplyPlanAvailabilityRequestSchema } from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { getWorkflowAutomationAvailability } from '@ebalance/proto/bffuser/v1/workflow_automation_service-WorkflowAutomationService_connectquery'
import { GetWorkflowAutomationAvailabilityRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_automation_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/** menu と route が共有する公開 gate の 3 状態。 */
export type ReleaseGateState = 'pending' | 'on' | 'off'

/** release flag ごとの gate。 値は menu の gate key と route の gate key を兼ねる。 */
export type ReleaseGate = 'workflowAutomation' | 'splitSupplyPlan'

/** 可用性 RPC の応答のうち gate が読む部分 (どの flag の応答も同じ形)。 */
type AvailabilityResult = UseQueryResult<
  { featureEnabled: boolean },
  ConnectError
>

/**
 * 可用性の query は毎回 mount で取り直し、 window focus でも取り直す。
 * flag の変更を数秒〜次の操作で反映するため、 cache を信用しない。
 */
export const RELEASE_QUERY_OPTIONS = {
  throwOnError: false,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
} as const

/**
 * 可用性 query の結果から 3 状態の gate を出す (flag の key に依らない唯一の判定)。
 * - 取得失敗 (評価失敗を含む) は `off` (fail-closed)
 * - この mount で取得し終えるまでは `pending` (隠すが redirect しない)
 * - 取得後は featureEnabled で `on` / `off`。 再取得中は直前の値を保つ
 *   (再取得中に false になる boolean だと、 route が home へ飛んでしまう)
 */
export function releaseGateState(
  result: Pick<AvailabilityResult, 'isError' | 'isFetchedAfterMount' | 'data'>,
): ReleaseGateState {
  if (result.isError) return 'off'
  if (!result.isFetchedAfterMount || result.data === undefined) return 'pending'
  return result.data.featureEnabled ? 'on' : 'off'
}

const WORKFLOW_AUTOMATION_REQUEST = create(
  GetWorkflowAutomationAvailabilityRequestSchema,
  {},
)
const SPLIT_SUPPLY_PLAN_REQUEST = create(
  GetSplitSupplyPlanAvailabilityRequestSchema,
  {},
)

const gateHooks: Record<ReleaseGate, () => ReleaseGateState> = {
  workflowAutomation: () =>
    releaseGateState(
      useQuery(
        getWorkflowAutomationAvailability,
        WORKFLOW_AUTOMATION_REQUEST,
        RELEASE_QUERY_OPTIONS,
      ),
    ),
  splitSupplyPlan: () =>
    releaseGateState(
      useQuery(
        getSplitSupplyPlanAvailability,
        SPLIT_SUPPLY_PLAN_REQUEST,
        RELEASE_QUERY_OPTIONS,
      ),
    ),
}

/**
 * flag の gate を返す。 `gate` は call site で固定の値を渡す
 * (render ごとに呼ぶ hook が変わらないようにする)。
 * pending / off / on の意味は [[releaseGateState]]。
 */
export function useReleaseGate(gate: ReleaseGate): ReleaseGateState {
  return gateHooks[gate]()
}
